import React, { useState, useEffect } from 'react';
import { Table, Tag, Button, Input, Select, Modal, Form, Row, Col, Empty, DatePicker, Switch, Tooltip, Upload, Progress } from 'antd';
import dayjs from 'dayjs';
import { PlusOutlined, EditOutlined, UploadOutlined, DeleteOutlined, EyeOutlined, CheckCircleOutlined, ClockCircleOutlined, WarningOutlined, BankOutlined } from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation, Trans } from 'react-i18next';
import i18n from '../../i18n';
import { libellesTraduits } from '../../i18n/libelles';
import { drapeauPays, trouverPays } from '../../variables/paysCedeao';
import TableExportButton from '../../components/TableExportButton';
import logoLocal from '../../assets/images/logo.png';
import { getAllOrganismes, getOrganismesPage } from '../../services/organismeService';
import PageHeader from '../../components/PageHeader';
import KpiCards from '../../components/KpiCards';

// Drapeau correspondant à un pays API (essaie code ISO puis code Carte Brune).
const flagPays = (codeIso, codeBcb) => drapeauPays(codeIso) || drapeauPays(codeBcb);

/**
 * Cle de pays normalisee d'un organisme.
 *
 * Le referentiel porte DEUX codes pays : `codePays` en ISO 3166 alpha-3 (« TGO »)
 * et `codePaysBCB` en code Carte Brune, alpha-2 (« TG »). Selon la fiche, l'un
 * ou l'autre peut etre vide. Comparer `codePays` a `codePays` rate donc toutes
 * les lignes qui ne renseignent que le second - c'est ce qui faussait le
 * rattachement des compagnies a leur bureau.
 *
 * On ramene les deux a l'ISO-3 via le referentiel CEDEAO, qui indexe les deux
 * alphabets. Un pays hors CEDEAO retombe sur son code brut : mieux vaut
 * regrouper sur une valeur non normalisee que ne pas regrouper du tout.
 */
const clePays = (o) => trouverPays(o?.codePays)?.code
  || trouverPays(o?.codePaysBCB)?.code
  || o?.codePays || o?.codePaysBCB || null;


const BRANDING_FIELD_MAP = {
  logo: 'logo',
  header: 'headerImageUrl',
  footer: 'footerImageUrl',
};

const BRANDING_LABELS = libellesTraduits('organismes:branding.libelles');

const BRANDING_DESCRIPTIONS = libellesTraduits('organismes:branding.descriptions');

/* 
   LIGHTBOX
 */
const ImageLightbox = ({ src, label, onClose }) => {
  const { t } = useTranslation('organismes');
  if (!src) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(6px)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        cursor: 'zoom-out',
      }}
    >
      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 12, letterSpacing: 1 }}>
        {t('image.cliquerPourFermer', { libelle: label.toUpperCase() })}
      </div>
      <img
        src={src}
        alt={label}
        style={{
          maxWidth: '88vw', maxHeight: '78vh',
          objectFit: 'contain', borderRadius: 10,
          boxShadow: '0 24px 80px rgba(0,0,0,0.7)',
          background: `repeating-conic-gradient(#555 0% 25%, #333 0% 50%) 0 0 / 20px 20px`,
        }}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};

/* 
   BRANDING UPLOADER - version enrichie
 */
const BrandingUploader = ({ trackingId, type, currentUrl, onChange, pendingMode = false }) => {
  const { t } = useTranslation('organismes');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imgMeta, setImgMeta] = useState(null); // { w, h }

  const apiBase = axiosInstance.defaults.baseURL || '';
  const previewUrl = currentUrl
    ? `${apiBase.replace(/\/$/, '')}${currentUrl}?v=${Date.now()}`
    : null;

  /* Lire les dimensions de l'image */
  useEffect(() => {
    if (!previewUrl) { setImgMeta(null); return; }
    const img = new Image();
    img.onload = () => setImgMeta({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => setImgMeta(null);
    img.src = previewUrl;
  }, [previewUrl]);

  const beforeUpload = (file) => {
    const ok = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type);
    if (!ok) { toast.error(t('image.formatNonSupporte')); return Upload.LIST_IGNORE; }
    if (file.size > 2 * 1024 * 1024) { toast.error(t('image.tropLourde')); return Upload.LIST_IGNORE; }
    return true;
  };

  const customRequest = async ({ file, onSuccess, onError }) => {
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    setProgress(0);
    try {
      const res = await axiosInstance.post(
        `/v1/organismes/${trackingId}/branding/${type}`,
        fd,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
          },
        }
      );
      const fieldName = BRANDING_FIELD_MAP[type];
      onChange(res.data?.data?.[fieldName] || null);
      toast.success(t('image.televersee'));
      onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('image.echecTeleversement'));
      onError?.(e);
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 800);
    }
  };

  const handleDelete = async () => {
    setUploading(true);
    try {
      await axiosInstance.delete(`/v1/organismes/${trackingId}/branding/${type}`);
      onChange(null);
      toast.success(t('image.supprimee'));
    } catch (e) {
      toast.error(e?.response?.data?.message || t('image.suppressionEchouee'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      {lightboxOpen && previewUrl && (
        <ImageLightbox src={previewUrl} label={BRANDING_LABELS[type]} onClose={() => setLightboxOpen(false)} />
      )}

      <div style={{
        border: '1px solid #e5e7eb',
        borderRadius: 10,
        padding: '14px 16px',
        marginBottom: 12,
        background: pendingMode ? '#fffdf5' : '#fff',
        position: 'relative',
        transition: 'box-shadow 0.2s',
      }}>
        {/* En-tête */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>{BRANDING_LABELS[type]}</span>
              {pendingMode && (
                <Tooltip title={t('image.disponibleApresCreationOrganisme')}>
                  <Tag icon={<ClockCircleOutlined />} color="warning" style={{ borderRadius: 20, fontSize: 11, margin: 0 }}>
                    {t('image.apresCreation')}
                  </Tag>
                </Tooltip>
              )}
              {!pendingMode && currentUrl && (
                <Tag icon={<CheckCircleOutlined />} color="success" style={{ borderRadius: 20, fontSize: 11, margin: 0 }}>
                  {t('image.configure')}
                </Tag>
              )}
              {!pendingMode && !currentUrl && (
                <Tag color="default" style={{ borderRadius: 20, fontSize: 11, margin: 0 }}>
                  {t('image.nonDefini')}
                </Tag>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2, paddingLeft: 22 }}>
              {BRANDING_DESCRIPTIONS[type]}
            </div>
          </div>
        </div>

        {/* Preview */}
        {!pendingMode && previewUrl && (
          <div style={{
            marginBottom: 10,
            borderRadius: 8,
            overflow: 'hidden',
            border: '1px solid #e5e7eb',
            background: `repeating-conic-gradient(#f0f0f0 0% 25%, #fafafa 0% 50%) 0 0 / 16px 16px`,
            position: 'relative',
            maxHeight: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-in',
          }}
            onClick={() => setLightboxOpen(true)}
          >
            <img
              src={previewUrl}
              alt=""
              style={{
                maxHeight: 100, maxWidth: '100%',
                objectFit: 'contain', padding: 8,
                display: 'block',
              }}
            />
            {/* Overlay au hover */}
            <div style={{
              position: 'absolute', inset: 0,
              background: 'rgba(0,0,0,0)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.2s',
            }}
              className="preview-overlay"
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.25)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0)'}
            >
              <EyeOutlined style={{ fontSize: 24, color: '#fff', opacity: 0, transition: 'opacity 0.2s' }}
                ref={el => {
                  if (el) {
                    const parent = el.closest('.preview-overlay') || el.parentElement;
                    parent.addEventListener('mouseenter', () => { el.style.opacity = 1; });
                    parent.addEventListener('mouseleave', () => { el.style.opacity = 0; });
                  }
                }}
              />
            </div>
            {/* Dimensions */}
            {imgMeta && (
              <div style={{
                position: 'absolute', bottom: 4, right: 6,
                fontSize: 10, color: 'rgba(0,0,0,0.45)',
                background: 'rgba(255,255,255,0.7)', borderRadius: 4, padding: '1px 5px',
              }}>
                {imgMeta.w} × {imgMeta.h} px
              </div>
            )}
          </div>
        )}

        {/* Placeholder si pas d'image (mode édition seulement) */}
        {!pendingMode && !previewUrl && (
          <div style={{
            marginBottom: 10, borderRadius: 8,
            border: '2px dashed #e5e7eb',
            height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#d1d5db', fontSize: 12,
          }}>
            {t('image.aucune')}
          </div>
        )}

        {/* Barre de progression */}
        {uploading && progress > 0 && (
          <div style={{ marginBottom: 8 }}>
            <Progress
              percent={progress}
              size="small"
              strokeColor={{ from: '#1a6b3c', to: '#34d399' }}
              style={{ marginBottom: 0 }}
              showInfo={progress < 100}
              status={progress === 100 ? 'success' : 'active'}
            />
          </div>
        )}

        {/* Actions */}
        {!pendingMode && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Upload
              accept="image/png,image/jpeg,image/webp"
              beforeUpload={beforeUpload}
              customRequest={customRequest}
              showUploadList={false}
              maxCount={1}
              disabled={uploading}
            >
              <Button
                icon={<UploadOutlined />}
                loading={uploading}
                size="small"
                style={{ borderRadius: 6 }}
                type={currentUrl ? 'default' : 'primary'}
                {...(!currentUrl && { style: { borderRadius: 6, background: '#1a6b3c', borderColor: '#1a6b3c' } })}
              >
                {currentUrl ? t('image.remplacer') : t('image.televerser')}
              </Button>
            </Upload>
            {currentUrl && (
              <>
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => setLightboxOpen(true)}
                  style={{ borderRadius: 6 }}
                >
                  {t('image.apercu')}
                </Button>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleDelete}
                  loading={uploading}
                  style={{ borderRadius: 6 }}
                >
                  {t('image.supprimer')}
                </Button>
              </>
            )}
            <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 'auto' }}>
              {t('image.formats')}
            </span>
          </div>
        )}

        {/* Mode création : bouton désactivé avec tooltip */}
        {pendingMode && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Tooltip title={t('image.creerDabord')}>
              <Button
                icon={<UploadOutlined />}
                size="small"
                disabled
                style={{ borderRadius: 6 }}
              >
                {t('image.televerser')}
              </Button>
            </Tooltip>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>
              {t('image.formats')}
            </span>
          </div>
        )}
      </div>
    </>
  );
};

/* 
   COMPOSANT PRINCIPAL
 */
const { Option } = Select;
const TYPES_ORGANISME = ['BUREAU_NATIONAL', 'COMPAGNIE_MEMBRE', 'BUREAU_HOMOLOGUE', 'AUTRE'];
const TYPE_LABEL = libellesTraduits('organismes:types');

/**
 * Le `code` d'un organisme n'est pas une etiquette d'affichage : il est REPRIS TEL QUEL
 * dans le numero local des sinistres TE, sous la forme
 * `CODE / 0234 / BF / 2026` (voir SinistreServiceImpl cote backend).
 *
 * Un code qui contient des espaces ou qui depasse une poignee de caracteres produit
 * donc des numeros du genre « SANLAM TOGO / 0234 / BF / 2026 », ou l'on croit lire
 * une raison sociale au milieu d'une reference. Le generateur, lui, fait deja ce
 * qu'il faut : il prend le code. C'est le referentiel qu'il faut corriger, et pour
 * cela encore faut-il voir lesquels posent probleme.
 */
const codeInapteAuNumero = (code) => {
  const v = (code || '').trim();
  if (!v) return i18n.t('organismes:codeNumero.absent');
  if (/\s/.test(v)) return i18n.t('organismes:codeNumero.espace');
  if (v.length > 10) return i18n.t('organismes:codeNumero.tropLong', { longueur: v.length });
  return null;
};

export default function Organismes() {
  const { t } = useTranslation('organismes');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Referentiel complet, charge independamment des filtres et de la pagination.
  // Les indicateurs comptent le referentiel, pas la page affichee : sinon
  // « Bureaux homologues » vaudrait 20 des qu'on ouvre la page 1 d'un filtre.
  const [tous, setTous] = useState([]);
  const [chargementTous, setChargementTous] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [paysFiltre, setPaysFiltre] = useState();
  const [typeFiltre, setTypeFiltre] = useState();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pays, setPays] = useState([]);
  const [form] = Form.useForm();

  // Pagination serveur (active en mode filtré par type).
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const reload = () => setRefreshKey((k) => k + 1);

  const paysByIso = React.useMemo(() => {
    const m = {};
    pays.forEach(p => { if (p.codeIso) m[p.codeIso] = p; });
    return m;
  }, [pays]);
  const paysById = React.useMemo(() => {
    const m = {};
    pays.forEach(p => { m[p.paysTrackingId] = p; });
    return m;
  }, [pays]);

  // Mode serveur (paginé) dès qu'un type est sélectionné ; sinon vue groupée (tout).
  const serverMode = !!typeFiltre;
  const codePaysFiltre = paysFiltre ? paysById[paysFiltre]?.codeIso : undefined;

  // Référentiel pays (une seule fois).
  useEffect(() => {
    axiosInstance.get('/v1/pays')
      .then((r) => setPays(r.data?.data || []))
      .catch(() => setPays([]));
  }, []);

  // Recherche débouncée (utilisée côté serveur et côté client).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Retour page 1 quand un filtre change.
  useEffect(() => { setPage(1); }, [typeFiltre, paysFiltre, debouncedSearch]);

  // Référentiel complet : source des indicateurs (toujours) et du tableau en
  // vue groupée. Volontairement hors du mode serveur - les compteurs doivent
  // rester stables quand on filtre ou qu'on change de page.
  useEffect(() => {
    let ignore = false;
    setChargementTous(true);
    getAllOrganismes()
      .then((all) => { if (!ignore) setTous(all); })
      .catch(() => { if (!ignore) setTous([]); })
      .finally(() => { if (!ignore) setChargementTous(false); });
    return () => { ignore = true; };
  }, [refreshKey]);

  // Vue groupée (aucun type sélectionné) : le tableau lit le référentiel déjà
  // en mémoire, filtré/paginé côté client - pas de second appel réseau.
  useEffect(() => {
    if (serverMode) return;
    setItems(tous);
    setTotal(0);
  }, [serverMode, tous]);

  // Vue filtrée par type : page récupérée côté serveur (type + pays + recherche).
  useEffect(() => {
    if (!serverMode) return;
    let ignore = false;
    setLoading(true);
    getOrganismesPage({
      page: page - 1, size: pageSize,
      type: typeFiltre, codePays: codePaysFiltre, search: debouncedSearch || undefined,
    })
      .then((res) => { if (!ignore) { setItems(res.items); setTotal(res.total); } })
      .catch(() => { if (!ignore) { setItems([]); setTotal(0); } })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [serverMode, page, pageSize, typeFiltre, codePaysFiltre, debouncedSearch, refreshKey]);

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ active: true, contacts: [], langue: 'PAYS' });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    const paysMatch = pays.find(p =>
      (record.codePays && p.codeIso === record.codePays) ||
      (record.codePaysBCB && p.codeCarteBrune === record.codePaysBCB)
    );
    form.setFieldsValue({
      typeOrganisme: record.typeOrganisme,
      raisonSociale: record.raisonSociale,
      code: record.code,
      suffixeComptable: record.suffixeComptable,
      email: record.email,
      responsable: record.responsable,
      contacts: record.contacts || [],
      paysTrackingId: paysMatch?.paysTrackingId,
      numeroAgrement: record.numeroAgrement,
      dateCreation: record.dateCreation ? dayjs(record.dateCreation) : null,
      apiEndpointUrl: record.apiEndpointUrl,
      active: record.active !== false,
      adresse: record.adresse,
      boitePostale: record.boitePostale,
      ville: record.ville,
      telephonePrincipal: record.telephonePrincipal,
      fax: record.fax,
      siteWeb: record.siteWeb,
      titreResponsable: record.titreResponsable,
      afficherDeuxSignatures: record.afficherDeuxSignatures === true,
      responsable2: record.responsable2,
      titreResponsable2: record.titreResponsable2,
      langue: record.langue || 'PAYS',
    });
    setModalOpen(true);
  };

  const updateBrandingUrl = (type, url) => {
    if (!editing) return;
    const fieldName = BRANDING_FIELD_MAP[type];
    setEditing(prev => prev ? { ...prev, [fieldName]: url } : prev);
    setItems(prev => prev.map(i =>
      i.organismeTrackingId === editing.organismeTrackingId
        ? { ...i, [fieldName]: url }
        : i
    ));
  };

  const afficherDeuxWatch = Form.useWatch('afficherDeuxSignatures', form);
  // Langue du pays choisi, pour dire a l'agent ce que "langue du pays" donnera.
  const langueDuPays = paysById[Form.useWatch('paysTrackingId', form)]?.langue;

  const submit = async (vals) => {
    try {
      const paysSelectionne = paysById[vals.paysTrackingId];
      const payload = {
        typeOrganisme: vals.typeOrganisme,
        raisonSociale: vals.raisonSociale,
        code: vals.code,
        email: vals.email,
        responsable: vals.responsable || null,
        contacts: vals.contacts || [],
        codePays: paysSelectionne?.codeIso || null,
        codePaysBCB: paysSelectionne?.codeCarteBrune || null,
        paysId: null,
        dateCreation: vals.dateCreation ? vals.dateCreation.format('YYYY-MM-DD') : null,
        numeroAgrement: vals.numeroAgrement || null,
        apiEndpointUrl: vals.apiEndpointUrl || null,
        active: vals.active !== false,
        adresse: vals.adresse || null,
        boitePostale: vals.boitePostale || null,
        ville: vals.ville || null,
        telephonePrincipal: vals.telephonePrincipal || null,
        fax: vals.fax || null,
        siteWeb: vals.siteWeb || null,
        titreResponsable: vals.titreResponsable || null,
        afficherDeuxSignatures: vals.afficherDeuxSignatures === true,
        responsable2: vals.afficherDeuxSignatures ? (vals.responsable2 || null) : null,
        titreResponsable2: vals.afficherDeuxSignatures ? (vals.titreResponsable2 || null) : null,
        suffixeComptable: vals.suffixeComptable ? vals.suffixeComptable.trim() : null,
        // PAYS efface la langue propre a l'organisme ; absente, le serveur la laisse telle quelle.
        langue: vals.langue || null,
      };
      if (editing) {
        await axiosInstance.put(`/v1/organismes/${editing.organismeTrackingId}`, payload);
        toast.success(t('messages.modifie'));
      } else {
        await axiosInstance.post('/v1/organismes', payload);
        toast.success(t('messages.cree'));
      }
      closeModal();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('messages.erreur'));
    }
  };

  // En mode serveur, `items` est déjà filtré/paginé côté backend → pas de re-filtrage.
  // En mode groupé (vue complète), on filtre côté client (recherche + pays).
  // Mémoïsé : sinon `filtered` (et donc treeData/parentKeys) change d'identité à
  // chaque rendu, ce qui ferait re-déclencher l'effet d'expansion.
  const filtered = React.useMemo(() => {
    if (serverMode) return items;
    return items.filter(i => {
      const matchSearch = !debouncedSearch || i.raisonSociale?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        i.code?.toLowerCase().includes(debouncedSearch.toLowerCase());
      const paysSelectionne = paysFiltre ? paysById[paysFiltre] : null;
      const matchPays = !paysSelectionne || i.codePays === paysSelectionne.codeIso
        || i.codePaysBCB === paysSelectionne.codeCarteBrune;
      return matchSearch && matchPays;
    });
  }, [serverMode, items, debouncedSearch, paysFiltre, paysById]);

  const treeData = React.useMemo(() => {
    // Un filtre par type affiche la liste à plat (les enfants seraient exclus par le filtre).
    if (typeFiltre) return filtered;

    // Parents = bureaux (national + homologue). Chaque pays regroupe ses compagnies
    // sous son bureau, comme BNCB-TG. Le bureau national prime pour un même pays.
    const estBureau = (o) => o.typeOrganisme === 'BUREAU_NATIONAL' || o.typeOrganisme === 'BUREAU_HOMOLOGUE';
    const rang = (o) => (o.typeOrganisme === 'BUREAU_NATIONAL' ? 0 : 1);
    const bureaux = filtered.filter(estBureau).slice().sort((a, b) => rang(a) - rang(b));
    const autres = filtered.filter((o) => !estBureau(o));
    const attaches = new Set();
    const racines = bureaux.map((bureau) => {
      // Meme cle de pays que les indicateurs : sinon le tableau et la carte
      // « Compagnies membres » annoncent deux nombres differents.
      const paysBureau = clePays(bureau);
      const enfants = paysBureau
        ? autres.filter((o) => o.typeOrganisme === 'COMPAGNIE_MEMBRE'
          && clePays(o) === paysBureau
          && !attaches.has(o.organismeTrackingId))
        : [];
      enfants.forEach((e) => attaches.add(e.organismeTrackingId));
      // `_compagnies` (et non `children`) : rendu dans un sous-tableau via
      // expandedRowRender, pas en arbre imbriqué.
      return enfants.length ? { ...bureau, _compagnies: enfants } : bureau;
    });
    const orphelins = autres.filter((o) => !attaches.has(o.organismeTrackingId));
    return [...racines, ...orphelins];
  }, [filtered, typeFiltre]);

  /**
   * Indicateurs du référentiel.
   *
   * Le total (244) est déjà porté par l'en-tête ; le répéter en carte n'apprend
   * rien. Ce qu'on ne lit nulle part ailleurs, c'est la STRUCTURE du
   * référentiel : combien de compagnies le bureau national fédère, et combien
   * de bureaux homologues CEDEAO sont enregistrés.
   *
   * « Compagnies membres » = les compagnies rattachées au pays du bureau
   * national, c'est-à-dire celles qui lui sont intrinsèquement liées. Le pays
   * n'est pas codé en dur : il est lu sur la ligne BUREAU_NATIONAL (active en
   * priorité, comme le fait le backend), puis normalisé par `clePays`.
   *
   * Aucun repli silencieux : si le bureau national ou son pays est introuvable,
   * les cartes affichent « - ». Compter toutes les compagnies dans ce cas
   * donnait un nombre plausible et faux, ce qui est pire que pas de nombre.
   */
  const kpis = React.useMemo(() => {
    const parType = (t) => tous.filter((o) => o.typeOrganisme === t);
    const bureauNational = parType('BUREAU_NATIONAL').find((o) => o.active !== false)
      || parType('BUREAU_NATIONAL')[0];
    const paysNational = clePays(bureauNational);
    const compagnies = parType('COMPAGNIE_MEMBRE');
    const membres = paysNational
      ? compagnies.filter((o) => clePays(o) === paysNational).length
      : null;

    const homologues = parType('BUREAU_HOMOLOGUE');
    const paysHomologues = new Set(homologues.map(clePays).filter(Boolean)).size;
    const inactifs = tous.filter((o) => o.active === false).length;

    const libellePaysNational = paysNational
      ? (paysByIso[paysNational]?.libelle || trouverPays(paysNational)?.nom || paysNational)
      : null;

    return [
      {
        label: t('kpis.compagniesMembres'),
        value: membres == null ? '-' : membres,
        color: '#1a6b3c',
        suffix: libellePaysNational
          ? t('kpis.bureauNationalPays', { pays: libellePaysNational })
          : t('kpis.bureauNationalIntrouvable'),
        onClick: () => setTypeFiltre((v) => (v === 'COMPAGNIE_MEMBRE' ? undefined : 'COMPAGNIE_MEMBRE')),
        active: typeFiltre === 'COMPAGNIE_MEMBRE',
      },
      {
        label: t('kpis.bureauxHomologues'),
        value: homologues.length,
        color: '#7c3aed',
        suffix: paysHomologues
          ? t('kpis.paysRepresentes', { count: paysHomologues })
          : t('kpis.aucunPays'),
        onClick: () => setTypeFiltre((v) => (v === 'BUREAU_HOMOLOGUE' ? undefined : 'BUREAU_HOMOLOGUE')),
        active: typeFiltre === 'BUREAU_HOMOLOGUE',
      },
      {
        label: t('kpis.compagniesEtrangeres'),
        value: membres == null ? '-' : compagnies.length - membres,
        color: '#2563eb',
        suffix: t('kpis.rattacheesHomologue'),
      },
      {
        label: t('kpis.organismesInactifs'),
        value: inactifs,
        color: inactifs ? '#64748b' : '#1a6b3c',
        suffix: inactifs ? t('kpis.exclusSelections') : t('kpis.referentielActif'),
      },
    ];
  }, [tous, paysByIso, typeFiltre, t]);

  const typeColor = { BUREAU_NATIONAL: 'green', COMPAGNIE_MEMBRE: 'blue', BUREAU_HOMOLOGUE: 'purple', AUTRE: 'default' };

  const cols = [
    // Numero d'ordre dans la page affichee : repere de lecture pour designer une
    // ligne. Hors export, la numerotation n'a pas de sens dans un fichier qu'on retrie.
    {
      title: t('colonnes.numero'), key: 'ordre', width: 56, align: 'center', exportable: false,
      render: (_, __, index) => <span style={{ fontWeight: 700, color: '#94a3b8' }}>{index + 1}</span>,
    },
    {
      title: t('colonnes.code'), dataIndex: 'code', key: 'code', width: 150,
      render: (v) => {
        const probleme = codeInapteAuNumero(v);
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight: 700, color: probleme ? '#b45309' : '#1a6b3c' }}>{v || '-'}</span>
            {probleme && (
              <Tooltip title={probleme}>
                <WarningOutlined style={{ color: '#d97706' }} />
              </Tooltip>
            )}
          </span>
        );
      }
    },
    {
      title: t('colonnes.raisonSociale'), dataIndex: 'raisonSociale', key: 'rs', ellipsis: true,
      render: (v, r) => (
        <span style={{ fontWeight: 500 }}>
          {v}
          {r._compagnies?.length ? (
            <Tag style={{ marginLeft: 8, background: '#f0fdf4', color: '#1a6b3c', border: '1px solid #bbf7d0', borderRadius: 20 }}>
              {t('colonnes.nbCompagnies', { count: r._compagnies.length })}
            </Tag>
          ) : null}
        </span>
      )
    },
    {
      title: t('colonnes.type'), dataIndex: 'typeOrganisme', key: 'type', width: 180,
      render: (v) => <Tag color={typeColor[v] || 'default'} style={{ borderRadius: 20 }}>{TYPE_LABEL[v] || v}</Tag>,
      export: (v) => TYPE_LABEL[v] || v || ''
    },
    { title: t('colonnes.email'), dataIndex: 'email', key: 'email', width: 200, ellipsis: true, export: (v) => v || '' },
    {
      title: t('colonnes.pays'), key: 'pays', width: 140,
      render: (_, r) => {
        const lib = paysByIso[r.codePays]?.libelle || r.codePays || '-';
        const f = flagPays(r.codePays, r.codePaysBCB);
        return f ? `${f} ${lib}` : lib;
      },
      export: (_, r) => paysByIso[r.codePays]?.libelle || r.codePays || ''
    },
    {
      title: t('colonnes.actif'), dataIndex: 'active', key: 'active', width: 80, align: 'center',
      render: (v) => v ? <Tag color="green" style={{ borderRadius: 20 }}>{t('colonnes.oui')}</Tag> : <Tag color="red" style={{ borderRadius: 20 }}>{t('colonnes.non')}</Tag>,
      export: (v) => v ? t('colonnes.oui') : t('colonnes.non')
    },
    {
      title: '', key: 'actions', width: 70, align: 'center', fixed: 'right', exportable: false,
      render: (_, r) => (
        <Tooltip title={t('colonnes.modifier')}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} style={{ borderRadius: 6 }} />
        </Tooltip>
      ),
    },
  ];

  // Colonnes du sous-tableau « Compagnies d'assurance membres » d'un bureau.
  const sousColonnes = [
    {
      title: t('colonnes.code'), dataIndex: 'code', key: 'code', width: 150,
      render: (v) => {
        const probleme = codeInapteAuNumero(v);
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight: 700, color: probleme ? '#b45309' : '#1a6b3c' }}>{v || '-'}</span>
            {probleme && (
              <Tooltip title={probleme}>
                <WarningOutlined style={{ color: '#d97706' }} />
              </Tooltip>
            )}
          </span>
        );
      }
    },
    {
      title: t('colonnes.compagnie'), dataIndex: 'raisonSociale', key: 'rs', ellipsis: true,
      render: (v) => <span style={{ fontWeight: 500 }}>{v}</span>
    },
    { title: t('colonnes.email'), dataIndex: 'email', key: 'email', width: 220, ellipsis: true, render: (v) => v || '-' },
    {
      title: t('colonnes.actif'), dataIndex: 'active', key: 'active', width: 80, align: 'center',
      render: (v) => v ? <Tag color="green" style={{ borderRadius: 20 }}>{t('colonnes.oui')}</Tag> : <Tag color="red" style={{ borderRadius: 20 }}>{t('colonnes.non')}</Tag>
    },
    {
      title: '', key: 'actions', width: 70, align: 'center',
      render: (_, r) => (
        <Tooltip title={t('colonnes.modifier')}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} style={{ borderRadius: 6 }} />
        </Tooltip>
      ),
    },
  ];

  /*  Section branding réutilisable  */
  const renderBrandingSection = () => {
    const isPending = !editing;

    return (
      <Col span={24}>
        {/* Titre section */}
        <div style={{
          borderTop: '1px dashed #e5e7eb', margin: '4px 0 14px',
          paddingTop: 10, display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#1a6b3c', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('branding.titreSection')}
          </span>
          {isPending && (
            <Tag icon={<ClockCircleOutlined />} color="warning" style={{ borderRadius: 20, fontSize: 11 }}>
              {t('branding.disponibleApresCreation')}
            </Tag>
          )}
        </div>

        {/* Bandeau informatif en mode création */}
        {isPending && (
          <div style={{
            background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
            border: '1px solid #fde68a',
            borderRadius: 10,
            padding: '10px 14px',
            marginBottom: 14,
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
          }}>
            <WarningOutlined style={{ color: '#d97706', fontSize: 16, marginTop: 1, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e', marginBottom: 2 }}>
                {t('branding.bandeauTitre')}
              </div>
              <div style={{ fontSize: 12, color: '#b45309', lineHeight: 1.5 }}>
                <Trans t={t} i18nKey="branding.bandeauTexte" components={{ 1: <strong /> }} />
              </div>
            </div>
          </div>
        )}

        {/* Les 3 uploaders */}
        <div style={{
          background: isPending ? '#fafafa' : '#f8fafb',
          border: '1px solid #e5e7eb',
          borderRadius: 10,
          padding: 14,
          marginBottom: 12,
          opacity: isPending ? 0.85 : 1,
        }}>
          {['logo', 'header', 'footer'].map((type) => (
            <BrandingUploader
              key={type}
              trackingId={editing?.organismeTrackingId}
              type={type}
              currentUrl={
                type === 'logo' ? editing?.logo :
                  type === 'header' ? editing?.headerImageUrl :
                    editing?.footerImageUrl
              }
              onChange={(url) => updateBrandingUrl(type, url)}
              pendingMode={isPending}
            />
          ))}
        </div>
      </Col>
    );
  };

  return (
    <Layout title={t('page.titre')}>
      {/* Deux regimes de chargement cohabitent : en mode serveur (`typeFiltre` actif)
          seul `total` connait le nombre reel de lignes, la page courante n'en montre
          qu'une tranche ; sinon tout est en memoire et `filtered.length` fait foi.
          C'est la meme source que le bouton d'export plus bas. */}
      <PageHeader
        titre={t('page.titre')}
        icone={<BankOutlined />}
        compte={serverMode ? total : filtered.length}
        unite={t('page.unite')}
        actions={(
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}
            style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}>
            {t('actions.ajouter')}
          </Button>
        )}
      />

      <KpiCards items={kpis} columns={4} />

      <div className="cb-card" style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Input.Search placeholder={t('filtres.rechercher')} value={search}
          onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 400, flex: 1, minWidth: 240 }} allowClear />
        <Select showSearch allowClear placeholder={t('filtres.pays')} value={paysFiltre} onChange={setPaysFiltre}
          style={{ minWidth: 220 }} filterOption={(i, o) => o.children.toLowerCase().includes(i.toLowerCase())}>
          {pays.map(p => <Option key={p.paysTrackingId} value={p.paysTrackingId}>{`${flagPays(p.codeIso, p.codeCarteBrune)} ${p.libelle}`.trim()}</Option>)}
        </Select>
        <Select allowClear placeholder={t('filtres.type')} value={typeFiltre} onChange={setTypeFiltre} style={{ minWidth: 220 }}>
          {TYPES_ORGANISME.map(t => <Option key={t} value={t}>{TYPE_LABEL[t]}</Option>)}
        </Select>
      </div>

      <div className="cb-card" style={{ padding: 0 }}>
        <Table
          className="cb-table"
          dataSource={treeData}
          columns={cols}
          rowKey="organismeTrackingId"
          loading={serverMode ? loading : chargementTous}
          onChange={(pag) => { setPage(pag.current); setPageSize(pag.pageSize); }}
          pagination={{
            ...(serverMode ? { current: page, total } : {}),
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (n) => t('liste.total', { count: n }),
          }}
          expandable={{
            defaultExpandAllRows: false,
            rowExpandable: (record) => record._compagnies?.length > 0,
            expandedRowRender: (record) => (
              <div style={{ background: '#f9fafb', padding: '8px 12px 8px 40px' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
                  {t('liste.compagniesMembres', { nombre: record._compagnies.length })}
                </div>
                <Table
                  className="cb-table"
                  rowKey="organismeTrackingId"
                  dataSource={record._compagnies}
                  columns={sousColonnes}
                  pagination={false}
                  size="small"
                />
              </div>
            ),
          }}
          locale={{ emptyText: <Empty description={t('liste.aucun')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        />
      </div>

      <TableExportButton
        floating
        data={filtered}
        dataLoader={async () => {
          // Export complet (toutes les pages) : on recharge tout puis on applique les filtres courants.
          const all = await getAllOrganismes();
          const q = debouncedSearch.toLowerCase();
          return all.filter((o) =>
            (!typeFiltre || o.typeOrganisme === typeFiltre)
            && (!codePaysFiltre || o.codePays === codePaysFiltre)
            && (!q
              || (o.raisonSociale || '').toLowerCase().includes(q)
              || (o.code || '').toLowerCase().includes(q)
              || (o.email || '').toLowerCase().includes(q)));
        }}
        columns={cols}
        filename="organismes"
        title={t('page.titre')}
        subtitle={typeFiltre ? TYPE_LABEL[typeFiltre] : t('page.sousTitreExport')}
        count={serverMode ? total : filtered.length}
        logo={logoLocal}
      />

      <Modal
        title={editing ? t('modale.titreModification', { nom: editing.raisonSociale }) : t('actions.ajouter')}
        open={modalOpen} onCancel={closeModal} footer={null} width={780} destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={submit} initialValues={{ active: true, contacts: [] }}>
          <Row gutter={16}>
            {/*  Infos générales  */}
            <Col span={12}>
              <Form.Item name="typeOrganisme" label={t('formulaire.type')} rules={[{ required: true }]}>
                <Select placeholder={t('formulaire.typePlaceholder')}>
                  {TYPES_ORGANISME.map(t => <Option key={t} value={t}>{TYPE_LABEL[t]}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="code" label={t('formulaire.code')} rules={[{ required: true }]}
                extra={t('formulaire.codeAide')}>
                <Input placeholder={t('formulaire.codeExemple')} maxLength={20} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="suffixeComptable" label={t('formulaire.suffixeComptable')}
                rules={[{ pattern: /^[0-9]{2,4}$/, message: t('formulaire.suffixeComptableRegle') }]}
                extra={t('formulaire.suffixeComptableAide')}>
                <Input placeholder={t('formulaire.suffixeComptableExemple')} maxLength={4} />
              </Form.Item>
            </Col>
            <Col span={12} />
            <Col span={24}>
              <Form.Item name="raisonSociale" label={t('formulaire.raisonSociale')} rules={[{ required: true }]}>
                <Input placeholder={t('formulaire.raisonSocialeExemple')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="email" label={t('formulaire.email')}
                rules={[{ required: true, type: 'email', message: t('formulaire.emailRegle') }]}>
                <Input placeholder="contact@organisme.tg" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="responsable" label={t('formulaire.responsable')}>
                <Input placeholder={t('formulaire.responsablePlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="contacts" label={t('formulaire.contacts')} extra={t('formulaire.contactsAide')}>
                <Select mode="tags" tokenSeparators={[',', ';']} placeholder="+228... / contact@..." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="paysTrackingId" label={t('formulaire.pays')} rules={[{ required: true }]}>
                <Select showSearch placeholder={t('formulaire.paysPlaceholder')}
                  filterOption={(i, o) => o.children.toLowerCase().includes(i.toLowerCase())}>
                  {pays.map(p => <Option key={p.paysTrackingId} value={p.paysTrackingId}>{`${flagPays(p.codeIso, p.codeCarteBrune)} ${p.libelle}`.trim()}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="langue" label={t('formulaire.langue')}
                tooltip={t('formulaire.langueAide')}>
                <Select
                  options={[
                    { value: 'PAYS', label: langueDuPays ? t('formulaire.langueDuPaysAvec', { langue: langueDuPays === 'EN' ? t('formulaire.langueAnglais') : t('formulaire.langueFrancais') }) : t('formulaire.langueDuPays') },
                    { value: 'FR', label: t('formulaire.langueFrancais') },
                    { value: 'EN', label: t('formulaire.langueAnglais') },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="numeroAgrement" label={t('formulaire.numeroAgrement')}>
                <Input placeholder={t('formulaire.numeroAgrementPlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="dateCreation" label={t('formulaire.dateCreation')}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabledDate={(d) => d && d.isAfter(dayjs())} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="apiEndpointUrl" label={t('formulaire.apiEndpoint')}
                extra={t('formulaire.apiEndpointAide')}>
                <Input placeholder="https://api.bureauhomologue.org" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="active" label={t('formulaire.actif')} valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>

            {/*  Coordonnées  */}
            <Col span={24}>
              <div style={{
                borderTop: '1px dashed #e5e7eb', margin: '4px 0 12px',
                paddingTop: 8, fontSize: 12, fontWeight: 600, color: '#1a6b3c', textTransform: 'uppercase',
              }}>
                {t('formulaire.coordonnees')}
              </div>
            </Col>
            <Col span={16}>
              <Form.Item name="adresse" label={t('formulaire.adresse')}>
                <Input placeholder={t('formulaire.adresseExemple')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="boitePostale" label={t('formulaire.boitePostale')}>
                <Input placeholder={t('formulaire.boitePostaleExemple')} maxLength={50} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="ville" label={t('formulaire.ville')}>
                <Input placeholder={t('formulaire.villeExemple')} maxLength={100} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="telephonePrincipal" label={t('formulaire.telephone')}>
                <Input placeholder={t('formulaire.telephoneExemple')} maxLength={30} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="fax" label={t('formulaire.fax')}>
                <Input placeholder={t('formulaire.faxExemple')} maxLength={30} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="siteWeb" label={t('formulaire.siteWeb')}>
                <Input placeholder={t('formulaire.siteWebExemple')} />
              </Form.Item>
            </Col>

            {/*  Signatures  */}
            <Col span={12}>
              <Form.Item name="titreResponsable" label={t('formulaire.titreResponsable')}
                extra={t('formulaire.titreResponsableAide')}>
                <Input placeholder={t('formulaire.titreResponsableExemple')} maxLength={150} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="afficherDeuxSignatures" label={t('formulaire.deuxSignatures')} valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            {afficherDeuxWatch && (
              <>
                <Col span={12}>
                  <Form.Item name="responsable2" label={t('formulaire.responsable2')}
                    rules={[{ required: true, message: t('formulaire.obligatoireDeuxSignatures') }]}>
                    <Input placeholder={t('formulaire.responsable2Placeholder')} maxLength={200} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="titreResponsable2" label={t('formulaire.titreResponsable2')}
                    rules={[{ required: true, message: t('formulaire.obligatoireDeuxSignatures') }]}>
                    <Input placeholder={t('formulaire.titreResponsable2Exemple')} maxLength={150} />
                  </Form.Item>
                </Col>
              </>
            )}

            {/*  Branding (création + édition)  */}
            {renderBrandingSection()}
          </Row>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
            <Button onClick={closeModal}>{t('actions.annuler')}</Button>
            <Button type="primary" htmlType="submit" style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
              {editing ? t('actions.enregistrer') : t('actions.creer')}
            </Button>
          </div>
        </Form>
      </Modal>
    </Layout>
  );
}